import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { Mail, Phone, MapPin, Star, MessageCircle, ArrowRight, Calendar } from 'lucide-react';
import { Scroll3DSection, Tilt3D } from './Scroll3D';

const Contact = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.6, 0.05, 0.01, 0.99],
      },
    },
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: t('contact.info.addressLabel'),
      value: t('contact.info.address'),
      color: 'from-neon-green to-neon-cyan',
    },
    {
      icon: Phone,
      title: t('contact.info.phoneLabel'),
      value: t('contact.info.phone'),
      color: 'from-neon-cyan to-neon-blue',
    },
    {
      icon: Mail,
      title: t('contact.info.emailLabel'),
      value: t('contact.info.email'),
      color: 'from-neon-blue to-neon-purple',
    },
  ];

  return (
    <section id="contact" ref={ref} className="py-10 bg-transparent dark:bg-transparent relative overflow-hidden">
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-40 h-40 bg-gradient-to-r from-neon-green/15 to-neon-cyan/15 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-gradient-to-r from-neon-blue/15 to-neon-purple/15 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-gradient-to-r from-neon-green/8 to-neon-purple/8 rounded-full blur-3xl"></div>
      </div>

      <Scroll3DSection className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="max-w-6xl mx-auto"
        >
          {/* Schedule a Call Card */}
          <motion.div variants={itemVariants} className="max-w-4xl mx-auto mb-16">
            <Tilt3D className="w-full h-full">
              <div className="bg-gradient-to-r from-neon-green/10 to-neon-cyan/10 backdrop-blur-xl border border-neon-green/30 rounded-3xl p-10 shadow-2xl shadow-neon-green/10 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-r from-neon-green/20 to-neon-cyan/20 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500"></div>
                <div className="relative z-10 text-center">
                  <div className="flex items-center justify-center space-x-4 rtl:space-x-reverse mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-neon-green to-neon-cyan rounded-2xl flex items-center justify-center shadow-2xl">
                      <Calendar className="w-8 h-8 text-black" />
                    </div>
                    <h3 className="text-3xl font-black text-white">
                      {t('contact.scheduleCall')}
                    </h3>
                  </div>
                  <p className="text-white/70 text-xl mb-8 leading-relaxed max-w-2xl mx-auto">
                    {t('contact.scheduleDescription')}
                  </p>
                  <motion.a
                    href="mailto:info@saham-holding.com"
                    whileHover={{ scale: 1.05, x: 5 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-gradient-to-r from-neon-green to-neon-cyan text-black px-10 py-5 rounded-2xl font-black text-xl hover:shadow-2xl hover:shadow-neon-green/30 transition-all duration-300 flex items-center space-x-3 rtl:space-x-reverse group/btn mx-auto cursor-pointer"
                  >
                    <span>{t('contact.scheduleCall')}</span>
                    <ArrowRight className="w-6 h-6 group-hover/btn:translate-x-1 transition-transform duration-300" />
                  </motion.a>
                </div>
              </div>
            </Tilt3D>
          </motion.div>

          {/* Enhanced Contact Information */}
          <div className="max-w-4xl mx-auto">
            <motion.div variants={itemVariants} className="grid lg:grid-cols-2 gap-8">
              {/* Enhanced Contact Info Cards */}
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 30 }}
                    animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
                    transition={{ delay: index * 0.2 + 0.5, duration: 0.8, type: "spring" }}
                    className="flex items-center space-x-4 rtl:space-x-reverse group"
                    whileHover={{ scale: 1.02, x: 8 }}
                  >
                    <div className={`w-12 h-12 bg-gradient-to-r ${info.color} rounded-xl flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300`}>
                      <info.icon className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h4 className="text-lg font-black text-white mb-1 group-hover:text-neon-green transition-colors duration-300">
                        {info.title}
                      </h4>
                      <p className="text-white/70 text-base font-semibold">
                        {info.value}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Enhanced Map Placeholder */}
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ delay: 1, duration: 0.8 }}
                className="w-full h-full"
              >
                <Tilt3D className="w-full h-full">
                  <div className="bg-black/50 backdrop-blur-xl border border-neon-green/30 rounded-3xl p-8 h-64 flex items-center justify-center shadow-2xl shadow-neon-green/10">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gradient-to-r from-neon-green to-neon-cyan rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-2xl">
                        <MapPin className="w-8 h-8 text-black" />
                      </div>
                      <h4 className="text-xl font-black text-white mb-3">
                        {t('contact.ourLocation')}
                      </h4>
                      <p className="text-white/70 text-base font-semibold mb-4">
                        {t('contact.info.address')}
                      </p>
                      <motion.a
                        href="https://maps.app.goo.gl/TQZsCX3pz3ZsC2d57"
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.05, x: 5 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center space-x-2 rtl:space-x-reverse text-neon-green hover:text-white font-bold transition-all duration-300 mx-auto cursor-pointer"
                      >
                        <span>{t('contact.viewMap')}</span>
                        <ArrowRight className="w-4 h-4" />
                      </motion.a>
                    </div>
                  </div>
                </Tilt3D>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </Scroll3DSection>
    </section>
  );
};

export default Contact; 